<template>
    <div :class="rowClass">
        <div class="text-center">
            <v-icon v-if="isSession" x-small :color="item.critical ? 'white' : 'green'" class="live-dot" title="Em andamento">fiber_manual_record</v-icon>
            {{ startTime }}
        </div>
        <div class="text-center">{{ item.transactionId ? `#${item.transactionId}` : "-" }}</div>
        <!-- Status junto dos alertas, para o que precisa de ação ficar no começo da linha -->
        <div class="text-no-wrap">
            <v-chip v-if="item.inconsistentHistory" x-small label color="white" text-color="red darken-3" class="font-weight-bold" title="Existe histórico com esta transação, mas a sessão continua ativa">
                <v-icon x-small left color="red darken-3">sync_problem</v-icon>HISTÓRICO GERADO
            </v-chip>
            <v-chip v-else-if="item.sessionError" x-small label color="white" text-color="red darken-3" class="font-weight-bold">
                <v-icon x-small left color="red darken-3">error</v-icon>ERRO NA SESSÃO
            </v-chip>
            <v-chip v-else x-small label :color="status.color" :text-color="status.textColor" class="font-weight-bold">
                <v-icon v-if="status.icon" x-small left :color="status.textColor">{{ status.icon }}</v-icon>{{ status.text }}
            </v-chip>
            <v-icon v-for="alert in alertIcons" :key="alert.title" small :class="['ml-1', { 'icon-blink': alert.blink }]" :color="item.critical ? 'white' : alert.color" :title="alert.title">{{ alert.icon }}</v-icon>
        </div>
        <div class="text-no-wrap">
            <a v-if="item.charger && item.plug" :href="format.nowUrl(item.charger, item.plug)" target="_blank" rel="noopener" class="charger-link" title="Abrir carregador em nova aba">
                <strong>{{ item.charger.name }}</strong> ({{ item.plug }})
            </a>
            <strong v-else>{{ item.charger ? item.charger.name : "-" }}</strong>
            <template v-if="isSession">
                <v-icon v-if="item.alerts.online" x-small class="ml-1" :color="item.critical ? 'white' : 'green'" title="Carregador online">wifi</v-icon>
                <v-icon v-else x-small class="ml-1 icon-blink" :color="item.critical ? 'white' : 'red'" title="Carregador sem comunicação">wifi_off</v-icon>
            </template>
        </div>
        <div class="text-truncate">{{ item.charger && item.charger.point ? item.charger.point.name : "-" }}</div>
        <div class="text-center text-no-wrap" :title="paymentTitle">
            <a v-if="item.payment" class="payment-link" @click="$emit('open-payment', item.payment.id)">
                <div>{{ money(item.payment.value) }}</div>
                <div class="caption payment-secondary">{{ paymentStatusLabel(item.payment.status) }}</div>
            </a>
            <span v-else>{{ item.paid ? "?" : "-" }}</span>
        </div>
        <div class="text-center">
            <v-icon v-if="item.failed" x-small color="red" title="Carregamento finalizado sem energia entregue">error</v-icon>
            {{ item.consumption ? Number(item.consumption).toFixed(isSession ? 1 : 2) : "0" }} kWh
        </div>
        <div class="text-center">{{ power }}</div>
        <div class="text-center">{{ format.time(item.minutesCharger) }}</div>
        <div :class="['text-center', { 'orange--text text--darken-2 font-weight-bold': isSession && item.alerts.idle && !item.critical }]" :title="isSession && item.alerts.idle ? `Ocioso há ${item.alerts.idleMinutes} min` : undefined">
            {{ format.time(item.minutesConnected) }}
        </div>
        <div class="text-center">{{ format.battery(item.percentStart, item.percent) }}</div>
        <div class="d-flex align-center text-no-wrap">
            <v-icon x-small :color="item.critical ? 'white' : undefined" class="mr-1" :title="unlockLabel">{{ unlockIcon }}</v-icon><span :title="item.user ? item.user.email : undefined">{{ userLabel }}</span>
            <v-spacer />
            <v-btn v-if="receipt" class="ml-2 receipt-btn" x-small icon title="Gerar recibo do carregamento (PDF)" @click="printReceipt">
                <v-icon small :color="item.critical ? 'white' : undefined">print</v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import UnlockBy from "@/enum/UnlockBy";
import statusCharger from "@/helpers/StatusCharger";

import { money, paymentStatusLabel } from "@/helpers/paymentAlerts";

import receiptPdf from "@/pdf/receipt";

import { LOW_POWER_RATIO } from "./shared/alerts";
import format from "./shared/format";
import { historyStatus } from "./shared/history";

export default {
    name: "ChargeRow",

    props: {
        item: { type: Object, required: true },
    },

    data() {
        return {
            format,
        };
    },

    methods: {
        money,
        paymentStatusLabel,
        printReceipt() {
            receiptPdf.download(receiptPdf.fromHistory(this.item.history, this.item.payment), this.item.transactionId);
        },
    },

    computed: {
        isSession() {
            return this.item.kind == "session";
        },
        rowClass() {
            return [
                "charge-grid",
                "charge-row",
                this.isSession ? "charge-row--session" : "charge-row--history",
                { "charge-row--failed": this.item.failed, "dashboard-alert": this.item.critical },
            ];
        },
        startTime() {
            // Mostra a data só quando não é de hoje
            const value = this.item.start;
            if (!value) return "-";
            return value.substr(0, 10) == this.$moment().format("DD/MM/YYYY") ? value.substr(11, 5) : value.substr(0, 16);
        },
        unlockIcon() {
            if (this.item.paid) return "credit_card";
            return this.item.nfc || this.item.unlockBy == UnlockBy.NFC ? "nfc" : "smartphone";
        },
        unlockLabel() {
            if (this.item.paid) return "Liberado por pagamento";
            return this.item.nfc || this.item.unlockBy == UnlockBy.NFC ? "Liberado por NFC" : "Liberado pelo aplicativo";
        },
        paymentTitle() {
            const payment = this.item.payment;
            if (!payment) return this.item.paid ? "Pagamento não encontrado" : "Sem pagamento";
            return `Pagamento #${payment.id} · ${payment.operator} · ${paymentStatusLabel(payment.status)}${payment.date ? ` · ${payment.date}` : ""} · clique para gerenciar`;
        },
        userLabel() {
            if (this.item.user) return format.shortEmail(this.item.user.email);
            return this.item.nfc ? `NFC ${this.item.nfc}` : "-";
        },
        receipt() {
            return !this.isSession && receiptPdf.canIssue(this.item.status);
        },
        status() {
            if (this.isSession) {
                const style = statusCharger.getComponentStyle(this.item.status);
                return { text: style.status, color: style.color, textColor: style.secondColor, icon: style.icon };
            }
            const style = historyStatus(this.item.status);
            return { text: style.text, color: style.color, textColor: "white", icon: "history" };
        },
        power() {
            if (this.isSession) {
                return this.item.status == "Charging" && this.item.power > 0 ? `${format.kW(this.item.power)} kW` : "-";
            }
            return this.item.maximumPower ? `máx ${format.kW(this.item.maximumPower)} kW` : "-";
        },
        alertIcons() {
            const icons = this.item.paymentAlerts.map((a) => ({ icon: a.critical ? "credit_card_off" : "payments", color: a.critical ? "red" : "orange darken-2", blink: a.critical, title: a.text }));
            if (!this.isSession) return icons;
            const alerts = this.item.alerts;
            if (this.item.inconsistentHistory) {
                const h = this.item.inconsistentHistory;
                icons.push({ icon: "sync_problem", color: "red", blink: true, title: `Histórico já gerado (${h.consumption} kWh às ${h.start}) e a sessão continua ativa` });
            }
            if (alerts.withoutConsumption) icons.push({ icon: "error", color: "red", blink: true, title: `Conectado há ${this.item.minutesConnected} minutos sem consumo` });
            if (alerts.slowStart) icons.push({ icon: "hourglass_bottom", color: "red", blink: true, title: `Liberado há ${this.item.minutesConnected} minutos e o carregamento não começou` });
            if (alerts.lowPower) icons.push({ icon: "trending_down", color: "red", blink: true, title: `Potência abaixo de ${LOW_POWER_RATIO * 100}% do máximo da sessão (${format.kW(this.item.maximumPower)} kW)` });
            if (alerts.idle) icons.push({ icon: "local_parking", color: "orange darken-2", blink: true, title: `Ocioso há ${alerts.idleMinutes} minutos após carregar (tolerância ${alerts.idleTolerance} min)` });
            return icons;
        },
    },
};
</script>

<style scoped>
.charge-row {
    border-bottom: 1px solid rgba(128, 128, 128, 0.2);
}

.charge-row--session {
    min-height: 44px;
    font-weight: 500;
    border-left: 4px solid #4caf50;
}

.charge-row--history {
    opacity: 0.75;
    border-left: 4px solid transparent;
}

.charge-row--failed {
    opacity: 1;
    background-color: rgba(244, 67, 54, 0.12);
}

.charger-link {
    color: inherit;
    text-decoration: none;
}

.charger-link:hover {
    text-decoration: underline;
}

.payment-link {
    display: block;
    color: inherit;
}

.payment-link:hover {
    text-decoration: underline;
}

.receipt-btn {
    opacity: 0.5;
}

.receipt-btn:hover {
    opacity: 1;
}

.payment-secondary {
    opacity: 0.7;
    line-height: 1;
}

.live-dot {
    animation: live-dot-pulse 1.5s ease-in-out infinite;
}

@keyframes live-dot-pulse {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
}

.icon-blink {
    animation: icon-blink 1s ease-in-out infinite;
}

@keyframes icon-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.2;
    }
}
</style>
